import React, { useContext, useEffect, useState } from 'react';

import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';

import { RSAKeyPair, RSAAPP, encryptedString } from '../../wee-scripts/RSAKeyPair';

import QuoterContext from '../../context/QuoterContext';

import Button from '../commons/Button';
import StopErrorModal from '../modals/StopErrorModal';

import axiosInstanceWee from '../../core/api-config';
import {
  formatToMoney,
  registerProgressInBackend,
  logProgressInVincoBack,
  scrollToFormStart,
} from '../../core/helper-functions';

import { FORM4_SCHEMA } from '../../constants/validation-schemas';
import { FORM4_SUBMIT_URL, OP_KEYS_URL } from '../../constants/constants';

import visaLogo from '../../assets/logos/visa-logo.webp';
import masterLogo from '../../assets/logos/mastercard-logo.webp';
import amexLogo from '../../assets/logos/amex-logo.png';
import opLogo from '../../assets/logos/openpay-logo.png';

const Form4 = () => {
  const [isTransactionDenied, setIsTransactionDenied] = useState(false);
  const { quoterContextValues, setQuoterContextValues } = useContext(QuoterContext);
  const { paymentPeriodicity, clientTotalPayment } = quoterContextValues.form3;
  const [publicKey, setPublicKey] = useState('');
  const [merchantId, setMerchantId] = useState('');
  const [sandboxMode, setSandboxMode] = useState(false);
  const [opDeviceSessionId, setOPDeviceSessionId] = useState('');
  const [errorModalMessage, setErrorModalMessage] = useState(
    'Ocurrió un error al procesar tu pago. Intenta con otra tarjeta o de nuevo más tarde.',
  );
  const currentYear = new Date().getFullYear();
  const expiracyYearsList = [...Array(10).keys()].map((element, index) => {
    const yearValue = (currentYear + index).toString().slice(-2);
    return <option value={yearValue}>{currentYear + index}</option>;
  });

  const formInitialName = (
    quoterContextValues.form1.Nombre
    && quoterContextValues.form1.Paterno
    && quoterContextValues.form1.Materno
  )
    ? `${quoterContextValues.form1.Nombre} ${quoterContextValues.form1.Paterno} ${quoterContextValues.form1.Materno}`.trim()
    : '';
  const formInitialValues = {
    ...quoterContextValues.form4,
    name: formInitialName || '',
    email: quoterContextValues.form1.Email || '',
  };
  const formValidationSchema = Yup.object().shape(FORM4_SCHEMA);
  const browserHistory = useHistory();

  const configureOpenpay = async () => {
    const keysPromise = await axiosInstanceWee
      .post(OP_KEYS_URL, new URLSearchParams({
        idTramite: quoterContextValues.ids.idTramite,
      }));

    let newMerchantId = '';
    let newPublickKey = '';
    let newSandboxMode = true;

    if (!keysPromise.data.dsRespuesta) {
      const keys = keysPromise.data.Mensaje.split(':');
      [newPublickKey, newMerchantId] = keys;
    } else {
      newPublickKey = keysPromise.data.dsRespuesta.Datos[0].PUBLIC_KEY;
      newMerchantId = keysPromise.data.dsRespuesta.Datos[0].MERCHANT_ID;
      newSandboxMode = !(keysPromise.data.dsRespuesta.Datos[0].isProduction === 1);
    }

    /* eslint-disable no-undef */
    OpenPay.setId(newMerchantId);
    OpenPay.setApiKey(newPublickKey);
    OpenPay.setSandboxMode(newSandboxMode);
    const deviceSessionId = OpenPay.deviceData.setup('payment-form', 'deviceIdHiddenFieldName');
    /* eslint-enable no-undef */
    setMerchantId(newMerchantId);
    setPublicKey(newPublickKey);
    setSandboxMode(newSandboxMode);
    setOPDeviceSessionId(deviceSessionId);
  };

  useEffect(() => {
    if (!publicKey && !merchantId && !sandboxMode) {
      configureOpenpay();
    }

    scrollToFormStart();
  }, []);

  const backwardsClickHandle = () => {
    setQuoterContextValues({
      ...quoterContextValues,
      ids: {
        ...quoterContextValues.ids,
        idPaso: 3,
      },
    });
    browserHistory.push('cotizador-3');
  };

  const closeModalHandle = () => {
    setIsTransactionDenied(false);
  };

  const opErrorHandler = (err, setFieldError) => {
    const errorCode = err.data.error_code;
    const cardErrorCodes = [
      '1003',
      '3001',
      '3002',
      '3003',
      '3004',
      '3005',
      '3008',
      '3009',
      '3010',
    ];
    if (cardErrorCodes.includes(errorCode)) {
      switch (errorCode) {
        case '1003':
        case '3001':
        case '3003':
        case '3004':
        case '3009':
        default:
          setFieldError('cardNumber', 'Tarjeta declinada. Por favor elija otra');
          break;
        case '3002':
          setFieldError('cardMonth', 'La tarjeta ha expirado');
          setFieldError('cardYear', 'La tarjeta ha expirado');
          break;
        case '3005':
          setFieldError('cardNumber', 'La tarjeta ha sido rechazada por el sistema antifraudes');
          break;
        case '3008':
          setFieldError('cardNumber', 'La tarjeta no está autorizada para en transacciones en linea');
          break;
        case '3010':
          setFieldError('cardNumber', 'El banco ha restringido la tarjeta');
          break;
      }
    } else {
      setIsTransactionDenied(true);
    }
    const newQuoterContext = { ...quoterContextValues };
    setQuoterContextValues({
      ...newQuoterContext,
      isAppLoading: false,
    });
  };

  const opSuccessHandle = async (data, values, setFieldError) => {
    // eslint-disable-next-line no-undef
    let newQuoterValues = { ...quoterContextValues };
    const key = new RSAKeyPair('10001',
      '10001',
      '4fe815426b577f69a6abde3a9e62b005863ef297e00d36c70e40f6aab43ee1216348161e548aa6ca6118c1f0d82140756d243a7c7ee45425f8b4d2bab3284a2570e55c6ecb1ad29251d4b097cc91e8cc5ffb1800b93b89d3679aa15fa89fb3258200e466f62881566987f44737dbe082b3f5ed60baaeefd8f34f98b5a251539b',
      1024);
    const requestCardTokenEncripted = btoa(
      encryptedString(key, data.id, RSAAPP.PKCS1Padding, RSAAPP.RawEncoding),
    );

    const submitValues = new URLSearchParams({
      idRegistro: quoterContextValues.ids.idTramite,
      Nombre: values.name,
      Email: values.email,
      RequestCard: requestCardTokenEncripted,
      DeviceSessionId: opDeviceSessionId,
      TipoSite: 0,
    });

    const submitFormPromise = await axiosInstanceWee.post(FORM4_SUBMIT_URL, submitValues);
    let isPaymentSuccessful = false;
    if (
      submitFormPromise.data.NoFilas > 0
      && parseInt(submitFormPromise.data.dsRespuesta.Datos[0].Cod, 10) === 101
    ) {
      isPaymentSuccessful = true;
    } else {
      const codMessage = submitFormPromise.dsRespuesta
        ? submitFormPromise.dsRespuesta.Datos[0].Cod.split(':')
        : [null, null];
      switch (codMessage[1]) {
        case '1003':
        case '3001':
        case '3003':
        case '3004':
        case '3009':
          setFieldError('cardNumber', 'Tarjeta declinada. Por favor elija otra');
          break;
        case '3002':
          setFieldError('cardMonth', 'La tarjeta ha expirado');
          setFieldError('cardYear', 'La tarjeta ha expirado');
          break;
        case '3005':
          setFieldError('cardNumber', 'La tarjeta ha sido rechazada por el sistema antifraudes');
          break;
        case '3008':
          setFieldError('cardNumber', 'La tarjeta no está autorizada para en transacciones en linea');
          break;
        case '3010':
          setFieldError('cardNumber', 'El banco ha restringido la tarjeta');
          break;
        default:
          setErrorModalMessage(
            codMessage[0] === 'ERT'
              ? 'Verifique los datos de su tarjeta'
              : 'No fue posible realizar el pago, en breve nos comunicaremos contigo',
          );
          setIsTransactionDenied(true);
      }
      newQuoterValues = {
        ...newQuoterValues,
        isAppLoading: false,
      };
      setQuoterContextValues(newQuoterValues);
      logProgressInVincoBack(
        newQuoterValues.ids.idTramite,
        new Date(),
        3,
        102,
        '002',
        submitFormPromise.status,
        submitFormPromise.status,
        null,
        submitFormPromise.data.Mensaje,
      );
    }

    if (isPaymentSuccessful) {
      newQuoterValues = {
        ...newQuoterValues,
        ids: {
          ...newQuoterValues.ids,
          idPaso: 5,
        },
        isAppLoading: false,
      };
      await registerProgressInBackend(newQuoterValues.ids.idTramite, 106);
      setQuoterContextValues(newQuoterValues);
      logProgressInVincoBack(
        newQuoterValues.ids.idTramite,
        new Date(),
        3,
        106,
        '002',
        submitFormPromise.status,
        submitFormPromise.status,
        null,
        submitFormPromise.data.Mensaje,
      );
      browserHistory.push('cotizador-5');
    }
  };

  const formSubmitHandle = (values, { setFieldError }) => {
    const newQuoterContext = { ...quoterContextValues };
    setQuoterContextValues({
      ...newQuoterContext,
      isAppLoading: true,
    });
    /* eslint-disable no-undef */
    OpenPay.token.create(
      {
        card_number: values.cardNumber,
        holder_name: values.name,
        expiration_year: values.cardYear,
        expiration_month: values.cardMonth,
        cvv2: values.cardCode,
      },
      (resp) => {
        opSuccessHandle(resp.data, values, setFieldError);
      },
      (err) => opErrorHandler(err, setFieldError),
      opDeviceSessionId,
    );
    /* eslint-enable no-undef */
  };

  return (
    <Formik
      initialValues={formInitialValues}
      validationSchema={formValidationSchema}
      onSubmit={formSubmitHandle}
      validateOnMount>
      {({ errors, touched, isSubmitting, isValid, isValidating }) => {
        return (
          <Form id="payment-form" className="quoter-form -step-4">
            {isTransactionDenied && (
              <StopErrorModal
                onClick={closeModalHandle}
                title="Error"
                body={errorModalMessage} />
            )}
            <h2>Genera tu pago</h2>
            <div className="client-payment">
              <span>{`Cargo ${paymentPeriodicity || '--'}:`}</span>
              <span>{formatToMoney(clientTotalPayment, true) || '--'}</span>
            </div>
            <div className="form-fields">
              <div className="field-row">
                <div className="field -input">
                  <label htmlFor="name">Nombre completo:</label>
                  {errors.name && touched.name && <div className="field-invalid">{errors.name}</div>}
                  {!errors.name && touched.name && <div className="field-valid">ok</div>}
                  <Field name="name" type="text" />
                </div>
                <div className="field -input">
                  <label htmlFor="email">Correo electrónico:</label>
                  {errors.email && touched.email && <div className="field-invalid">{errors.email}</div>}
                  {!errors.email && touched.email && <div className="field-valid">ok</div>}
                  <Field name="email" type="text" />
                </div>
              </div>

              <div className="field-row">
                <div className="field -input">
                  <label htmlFor="email">Número de tarjeta:</label>
                  {errors.cardNumber && touched.cardNumber && <div className="field-invalid">{errors.cardNumber}</div>}
                  {!errors.cardNumber && touched.cardNumber && <div className="field-valid">ok</div>}
                  <Field
                    maxLength={19}
                    name="cardNumber"
                    type="text" />
                </div>
                <div className="field -double-select">
                  <label htmlFor="none">Fecha de expiración</label>
                  {/* {
                    (errors.cardMonth || errors.cardYear)
                    && (touched.cardMonth || touched.cardYear)
                    && <div className="field-invalid">{errors.cardMonth}</div>
                  }
                  {!errors.insuredMunicipality && touched.insuredMunicipality &&
                  <div className="field-valid">ok</div>} */}
                  <div className="inputs">
                    <Field as="select" name="cardMonth">
                      <option value="">Elegir mes</option>
                      <option value="01">1</option>
                      <option value="02">2</option>
                      <option value="03">3</option>
                      <option value="04">4</option>
                      <option value="05">5</option>
                      <option value="06">6</option>
                      <option value="07">7</option>
                      <option value="08">8</option>
                      <option value="09">9</option>
                      <option value="10">10</option>
                      <option value="11">11</option>
                      <option value="12">12</option>
                    </Field>
                    <Field as="select" name="cardYear">
                      <option value="">Elegir año</option>
                      {expiracyYearsList}
                    </Field>
                  </div>
                </div>
              </div>

              <div className="field-row">
                <div className="field -input">
                  <label htmlFor="name">Código de seguridad</label>
                  {errors.cvv && touched.cvv && <div className="field-invalid">{errors.cvv}</div>}
                  {!errors.cvv && touched.cvv && <div className="field-valid">ok</div>}
                  <Field
                    maxLength={4}
                    name="cardCode"
                    type="text" />
                </div>
                <div className="field -card-images">
                  <img src={visaLogo} alt="Visa" />
                  <img src={masterLogo} alt="Mastercard" />
                  <img src={amexLogo} alt="American Express" />
                  <img src={opLogo} alt="Openpay" />
                </div>
              </div>
            </div>
            <div className="form-buttons">
              <Button
                onClick={backwardsClickHandle}
                type="button"
                label="Regresar"
                direction="backward" />
              <Button
                type="submit"
                disabled={!isValid && !isValidating && !isSubmitting}
                label="Pagar"
                direction="forward" />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default Form4;
